import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@shared/hooks/usePermissions';
import { ReceiveApiAdapter } from '../adapters/ReceiveApiAdapter';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';

export const useLocateReturned = (returnedId: number) => {
  const { t } = useTranslation('locationordersDetailModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  // 🎯 Adapter para ubicar productos
  const receiveAdapter = new ReceiveApiAdapter(createHttpClient(apiClientWH));

  // 🎯 Mutation para ubicar un producto individual
  const locateMutation = useMutation({
    mutationFn: (data: { product_id: number; qty: number; to_location_id: number; batch_id?: number }) =>
      receiveAdapter.locateProduct(returnedId, data),
    onSuccess: (response) => {
      toast({
        title: t('toasts.locate.success'),
        description: (response as any)?.message || t('toasts.locate.success'),
        variant: 'default',
      });

      // 🎯 Invalidar queries relacionadas para refrescar los datos
      queryClient.invalidateQueries({
        queryKey: ['locationOrderDetail', returnedId],
      });
      queryClient.invalidateQueries({ queryKey: ['logisticStatusCounts'] });
    },
    onError: (error: any) => {
      console.log('🔍 useLocateReturned - Error:', error);

      let errorMessage = t('toasts.locate.unexpected');

      // 🎯 Intentar usar el error_code para internacionalización
      const apiError = error?.response?.data?.error;
      if (apiError?.error_code) {
        const errorCodeKey = `toasts.api_errors.${apiError.error_code}`;

        // Extraer parámetros del mensaje según el tipo de error
        const translationParams: Record<string, string> = {};
        if (apiError.message) {
          // Para LOCATION_ORDER_LINE_NOT_FOUND: extraer product_id
          if (apiError.error_code === 'LOCATION_ORDER_LINE_NOT_FOUND') {
            const match = apiError.message.match(/product ID (\d+)/i) || apiError.message.match(/\d+/);
            translationParams.product_id = match ? match[1] || match[0] : '';
          }

          // Para QTY_MOVED_EXCEEDS_QTY_PLANNED: extraer qty_moved, qty_planned y product_id
          if (apiError.error_code === 'QTY_MOVED_EXCEEDS_QTY_PLANNED') {
            // Extraer qty_moved del primer paréntesis: "Quantity moved (2)"
            const qtyMovedMatch = apiError.message.match(/moved \((\d+)\)/i);
            translationParams.qty_moved = qtyMovedMatch ? qtyMovedMatch[1] : '';

            // Extraer qty_planned del segundo paréntesis: "quantity planned (1)"
            const qtyPlannedMatch = apiError.message.match(/planned \((\d+)\)/i);
            translationParams.qty_planned = qtyPlannedMatch ? qtyPlannedMatch[1] : '';

            // Extraer product_id del final: "product ID 939"
            const productIdMatch = apiError.message.match(/product ID (\d+)/i);
            translationParams.product_id = productIdMatch ? productIdMatch[1] : '';
          }

          // Para errores de excluded-products: extraer IDs y valores según corresponda
          if (apiError.error_code === 'SHOP_NOT_FOUND') {
            const idMatch = apiError.message.match(/ID (\d+)/i);
            translationParams.id = idMatch ? idMatch[1] : '';
          }

          if (apiError.error_code === 'EXTERNAL_PRODUCT_NOT_FOUND') {
            const idMatch = apiError.message.match(/product ID[: ]?(\d+)/i);
            translationParams.id = idMatch ? idMatch[1] : '';
          }

          if (apiError.error_code === 'EXTERNAL_PRODUCT_VARIANT_NOT_FOUND') {
            const idMatch = apiError.message.match(/variant ID[: ]?(\d+)/i);
            translationParams.id = idMatch ? idMatch[1] : '';
          }

          if (apiError.error_code === 'EXTERNAL_VARIANT_ALREADY_LINKED') {
            const idMatch = apiError.message.match(/ID[: ]?(\d+)/i);
            translationParams.id = idMatch ? idMatch[1] : '';
          }

          if (apiError.error_code === 'SHOP_DOES_NOT_BELONG_TO_TEAM') {
            const shopMatch = apiError.message.match(/Shop (\d+)/i);
            const teamMatch = apiError.message.match(/team (\d+)/i);
            translationParams.shop_id = shopMatch ? shopMatch[1] : '';
            translationParams.team_id = teamMatch ? teamMatch[1] : '';
          }

          if (apiError.error_code === 'EXCLUDED_PRODUCTS_AMOUNT_HANDLING_INVALID') {
            const valueMatch = apiError.message.match(/value[: ]?\{?([^}]+)\}?/i);
            translationParams.value = valueMatch ? valueMatch[1] : '';
          }

          // Para STOCK_MOVEMENT_ERROR: mensaje directo sin parámetros
          if (apiError.error_code === 'STOCK_MOVEMENT_ERROR') {
            // Sin placeholders específicos; se usará la traducción plana
          }
        }

        const translatedError = t(errorCodeKey, {
          defaultValue: null,
          ...translationParams,
        });

        // Si existe la traducción, usarla; si no, usar el mensaje de la API
        if (translatedError && translatedError !== errorCodeKey) {
          errorMessage = translatedError;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }
      } else if (apiError?.message) {
        // Si no hay error_code pero hay mensaje en error.error.message
        errorMessage = apiError.message;
      } else if (error?.response?.data?.message) {
        // Si hay mensaje directo en data.message
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        // Mensaje genérico del error
        errorMessage = error.message;
      }

      toast({
        title: t('toasts.locate.error'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // 🎯 Función wrapper para manejar callbacks
  const locateProduct = (
    data: {
      product_id: number;
      qty: number;
      to_location_id: number;
      batch_id?: number;
    },
    callbacks?: { onSuccess?: () => void; onError?: () => void },
  ) => {
    // 🎯 Verificar permiso antes de ubicar
    if (!hasPermission('location-orders.locate-picking-return')) {
      toast({
        title: t('toasts.locate.error'),
        description: t('toasts.locate.no_permission', {
          defaultValue: 'No tienes permiso para ubicar productos.',
        }),
        variant: 'destructive',
      });
      callbacks?.onError?.();
      return;
    }

    locateMutation.mutate(data, {
      onSuccess: () => {
        // Los toasts y invalidaciones se manejan en el onSuccess del mutation
        callbacks?.onSuccess?.();
      },
      onError: () => {
        // Los toasts de error se manejan en el onError del mutation
        callbacks?.onError?.();
      },
    });
  };

  return {
    locateProduct,
    isLocating: locateMutation.isPending,
  };
};
